import { useEffect, useState } from 'react';
import { Search, Edit, Trash2 } from 'lucide-react';
import { callApi } from '../services/ApiServices';
import AddCouponModal, { productOptions, discountTypeOptions } from '../components/common/AddCouponModal';
import toast from 'react-hot-toast';
import { ImagePath } from '../components/common/ImagePath';
import CommonPagination from '../components/common/CommonPagination';
import CommonLoader from '../components/common/CommonLoader';
import ConfirmationModal from '../components/common/ConfirmationModal';
import { useGlobalStates } from '../utlis/ClubsContext';
import { UserRole } from '../utlis/constant';
import { formatCurrency } from '../utlis/function';

// date input gives YYYY-MM-DD, API expects DD-MM-YYYY
const toDDMMYYYY = (date) => {
  if (!date) return "";
  const [year, month, day] = date.split("-");
  return `${day}-${month}-${year}`;
};

// API date (DD-MM-YYYY or ISO) -> YYYY-MM-DD for the date input
const toInputDate = (date) => {
  if (!date) return "";
  if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
    const [day, month, year] = date.split("-");
    return `${year}-${month}-${day}`;
  }
  const d = new Date(date);
  if (isNaN(d)) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (date) => (date ? toDDMMYYYY(toInputDate(date)) : "-");

const getProductLabel = (product) =>
  productOptions.find((option) => option.value === product)?.label || product || "-";

const formatDiscount = (coupon) => {
  if (coupon?.discountValue === undefined || coupon?.discountValue === null || coupon?.discountValue === "") return "-";
  return coupon?.discountType === "percentage"
    ? `${coupon.discountValue}%`
    : formatCurrency(coupon.discountValue);
};

const getUserName = (coupon) => coupon?.user?.name || coupon?.userName || "";
const getClubName = (coupon) => coupon?.club?.name || coupon?.name || "";

// Supports multi-club responses and the legacy single `club`.
const getCouponClubs = (coupon) => {
  const clubs = Array.isArray(coupon?.clubs)
    ? coupon.clubs
    : Array.isArray(coupon?.club)
      ? coupon.club
      : null;
  if (clubs?.length) {
    return clubs.map((club) => ({
      value: club?.id ?? club?.clubId,
      label: club?.name || club?.clubName || club?.id,
    }));
  }
  const clubId = coupon?.clubId || coupon?.club?.id;
  return clubId ? [{ value: clubId, label: getClubName(coupon) || clubId }] : [];
};

const getClubNames = (coupon) => getCouponClubs(coupon).map((club) => club.label).join(", ");

const Coupon = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [openCouponModal, setOpenCouponModal] = useState(false);
  const [selectedCouponForEdit, setSelectedCouponForEdit] = useState(null);
  const [couponIdToDelete, setCouponIdToDelete] = useState(null);
  const { userRole } = useGlobalStates();
  const [couponData, setCouponData] = useState([]);
  const limit = 10;
  const isViewer = userRole === UserRole.Viewer;

  useEffect(() => {
    fetchCouponListing(currentPage, limit, searchQuery);
  }, []);

  const fetchCouponListing = async (page, limit, search) => {
    let ApiUrl = `coupon?page=${page}&limit=${limit}`;
    if (search) {
      ApiUrl += `&search=${search}`;
    }
    if (!search) {
      setLoading(true);
    }
    try {
      const response = await callApi({
        method: "get",
        url: ApiUrl,
      });
      const totalItems = response?.pagination?.total;
      const totalPages = Math.ceil(totalItems / limit);
      setTotalCount(totalPages);
      setCouponData(response?.data);
    } catch (error) {
      console.error("Error fetching coupon listing:", error);
    } finally {
      setLoading(false);
    }
  };

  const buildCouponPayload = (formData) => ({
    clubId: (formData.clubs || []).map((club) => club.value),
    userId: formData.user?.value || "",
    product: formData.product?.value || "",
    couponCode: formData.code.trim(),
    validFrom: toDDMMYYYY(formData.validFrom),
    validTill: toDDMMYYYY(formData.validTo),
    usesCount: String(formData.usesCount),
    discountType: formData.discountType?.value || "",
    discountValue: String(formData.discountValue),
  });

  const onSaveCoupon = async (formData) => {
    try {
      setLoading(true);
      const response = await callApi({
        method: "post",
        url: `coupon`,
        data: buildCouponPayload(formData),
      });
      if (response.status === 200 || response.status === 201) {
        toast.success(response?.message || "Coupon created successfully");
        setOpenCouponModal(false);
        fetchCouponListing(currentPage, limit, searchQuery);
        return true;
      }
      return false;
    } catch (error) {
      console.log("error while creating coupon", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const onEditCoupon = async (formData) => {
    try {
      setLoading(true);
      const response = await callApi({
        method: "put",
        url: `coupon/${selectedCouponForEdit?.id}`,
        data: buildCouponPayload(formData),
      });
      if (response.status === 200 || response.status === 201) {
        toast.success(response?.message || "Coupon updated successfully");
        setSelectedCouponForEdit(null);
        fetchCouponListing(currentPage, limit, searchQuery);
        return true;
      }
      return false;
    } catch (error) {
      console.log("error while updating coupon", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (status, couponId) => {
    const newStatus = status === "active" ? "inactive" : "active";
    try {
      const res = await callApi({
        method: "patch",
        url: `coupon/${couponId}/status`,
        data: {
          status: newStatus,
        },
      });
      if (res.status === 200) {
        toast.success(res?.message || "Coupon status changed successfully", { id: "couponStatusToast" });
        fetchCouponListing(currentPage, limit, searchQuery);
      }
    } catch (error) {
      console.log("error while toggling coupon status", error);
    }
  };

  const onDeleteCoupon = async () => {
    try {
      setLoading(true);
      const response = await callApi({
        method: "patch",
        url: `coupon/${couponIdToDelete}/delete`,
        data: {},
      });
      if (response.status === 200 || response.status === 204) {
        toast.success(response?.message || "Coupon deleted successfully");
        setCouponIdToDelete(null);
        // step back a page if the last coupon on this page was deleted
        const page = couponData?.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
        setCurrentPage(page);
        fetchCouponListing(page, limit, searchQuery);
      }
    } catch (error) {
      console.log("error while deleting coupon", error);
    } finally {
      setLoading(false);
    }
  };

  const getEditInitialValues = (coupon) => {
    const userId = coupon?.userId || coupon?.user?.id;
    const product = productOptions.find((option) => option.value === coupon?.product);
    return {
      code: coupon?.couponCode || "",
      validFrom: toInputDate(coupon?.validFrom),
      validTo: toInputDate(coupon?.validTill),
      clubs: getCouponClubs(coupon),
      user: userId ? { value: userId, label: getUserName(coupon) || userId } : null,
      product: product || null,
      usesCount: coupon?.usesCount ?? "",
      discountType:
        discountTypeOptions.find((option) => option.value === coupon?.discountType) || discountTypeOptions[0],
      discountValue: coupon?.discountValue ?? "",
    };
  };

  const OnPageChange = (e) => {
    setCurrentPage(e);
    fetchCouponListing(e, limit, searchQuery);
  };

  return (
    <>
      {/* Search Bar */}
      {loading && <CommonLoader fullScreen />}
      <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6'>
        <div className="relative w-full sm:flex-1 sm:max-w-[320px]">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="search"
            name="search"
            placeholder="Search by coupon code..."
            value={searchQuery}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
              fetchCouponListing(1, limit, e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === " " && searchQuery?.length === 0) {
                e.preventDefault();
              }
            }}
            className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent text-base text-black placeholder-textcolor"
          />
        </div>
        <button
          onClick={() => setOpenCouponModal(true)}
          disabled={isViewer}
          className={`w-full sm:w-auto flex items-center justify-center gap-2 bg-primary hover:bg-hover text-white font-medium text-base px-6 py-3 rounded-lg transition-colors ${
            isViewer ? "cursor-not-allowed opacity-50" : ""
          }`}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 4V16M4 10H16"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Add Coupon
        </button>
      </div>
      {/* Table Container */}
      <div className="bg-white rounded-[12px] shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            {/* Table Header */}
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {["Code", "Valid From", "Valid To", "User Name", "Club Name", "Uses Count", "Discount", "Product", "Status", "Action"].map((heading) => (
                  <th key={heading} className="px-6 py-4 text-left text-base font-medium text-black whitespace-nowrap">
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody>
              {couponData?.length > 0 ? (
                couponData?.map((coupon, index) => (
                  <tr
                    key={coupon.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      index === couponData.length - 1 ? "border-b-0" : ""
                    }`}
                  >
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-black">
                        {coupon?.couponCode || "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-textcolor">{formatDisplayDate(coupon?.validFrom)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-textcolor">{formatDisplayDate(coupon?.validTill)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-textcolor">{getUserName(coupon) || "-"}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-textcolor">{getClubNames(coupon) || "-"}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-textcolor">{coupon?.usesCount ?? "-"}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-textcolor">{formatDiscount(coupon)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-textcolor">{getProductLabel(coupon?.product)}</span>
                    </td>

                    {/* status */}
                    <td className="px-6 py-4">
                      <button
                        type="button"
                        disabled={isViewer}
                        onClick={() => toggleStatus(coupon?.status, coupon.id)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 focus:outline-none
                            ${isViewer ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
                            ${coupon?.status === "active" ? "bg-[#4CAF50]" : "bg-gray-300"}
                          `}
                      >
                        <span
                          className={`inline-block h-3.5 w-3.5 rounded-full bg-white transform transition-transform duration-300
                             ${coupon?.status === "active" ? "translate-x-5" : "translate-x-1"}
                          `}
                        />
                      </button>
                    </td>

                    {/* Action Buttons */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setSelectedCouponForEdit(coupon)}
                          disabled={isViewer}
                          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 ${
                            isViewer ? "cursor-not-allowed opacity-50" : ""
                          }`}
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => setCouponIdToDelete(coupon.id)}
                          disabled={isViewer}
                          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                            isViewer ? "cursor-not-allowed opacity-50" : ""
                          }`}
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="px-6 py-10 text-center">
                    <div className="flex justify-center items-center">
                      <img
                        src={ImagePath?.NoDataFound}
                        alt="No Data Found"
                        style={{ width: 70, height: 70 }}
                      />
                    </div>
                    <p className="text-xl mt-2">No Data found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <ConfirmationModal
          isOpen={!!couponIdToDelete}
          title="Delete Coupon"
          description="Are you sure you want to delete this coupon? This action cannot be undone."
          confirmText="Delete"
          onConfirm={onDeleteCoupon}
          onCancel={() => setCouponIdToDelete(null)}
          isLoading={loading}
        />
        {openCouponModal && (
          <AddCouponModal
            onClose={() => setOpenCouponModal(false)}
            onSave={onSaveCoupon}
            isLoading={loading}
          />
        )}
        {selectedCouponForEdit && (
          <AddCouponModal
            title="Update Coupon"
            description="Edit the coupon details."
            onClose={() => setSelectedCouponForEdit(null)}
            onSave={onEditCoupon}
            isLoading={loading}
            initialValues={getEditInitialValues(selectedCouponForEdit)}
          />
        )}
      </div>
      <CommonPagination
        currentPage={currentPage}
        pageCount={totalCount}
        onPageChange={(e) => OnPageChange(e)}
      />
    </>
  );
};

export default Coupon;
